import React, { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { observer } from 'mobx-react-lite';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import { formatDate } from '../../lib/utils/DateHelper';
import { dateShort } from '../../assets/contants/dates-fns';
import { getStageProgress } from '../../features/process/lib/helpers';
import { Plus } from '../icons';
import { StageHeader } from './stage-header';

import type { Stage } from '../../app/entities/stage';
import { Step } from '../../app/entities/step';
import { Dot } from '../../features/process/components/step-status-selector';

import styles from './phase-content.module.scss';

interface PhaseProps {
  stage: Stage;
  projectId: string;
  currentStepId: string;
}

export const Phase: React.FC<PhaseProps> = observer(({ stage, projectId, currentStepId }) => {
  const navigate = useNavigate();
  const { sum, done } = getStageProgress(stage.steps);

  const handleAction = () => {
    Step.createShootingDay({
      projectId,
    });
  };

  const goTo = (stepId: string) => navigate(`../steps/${stepId}`);

  const isActiveStage = useMemo(() => {
    return !!stage.steps.find((step) => step._id === currentStepId);
  }, [stage, currentStepId]);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <div tabIndex={0}>
          <StageHeader
            name={stage.name}
            index={stage.order}
            total={sum}
            completed={done}
            isActive={isActiveStage}
          />
        </div>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content className={styles.content} sideOffset={8}>
          {stage.steps.map((step) => (
            <DropdownMenu.Item
              className={styles.item}
              key={step._id}
              onSelect={() => goTo(step._id)}
              data-active={step._id === currentStepId}
            >
              <div className={styles['dropdown-element-left']}>
                <div className={styles['step-status-wrapper']}>
                  <Dot data-status={step.status!} />
                </div>
                <div className={styles['name']}>{step.displayName}</div>
              </div>
              &nbsp;
              {step.startDate && <div className={styles['date-label']}>{formatDate(step.startDate, dateShort)}</div>}
            </DropdownMenu.Item>
          ))}

          {stage.name.toLowerCase() === 'production' && (
            <DropdownMenu.Item className={styles.item} onSelect={handleAction}>
              <div className={styles['dropdown-element-left']}>
                <div className={styles['check-item']}>
                  <Plus />
                </div>

                <div className={styles['name']}>Add shooting day</div>
              </div>
            </DropdownMenu.Item>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
});

